import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import {
    getScenarioChapters,
    type ScenarioChapterDef,
} from "@/content/scenarioChapters";

/**
 * 장 진입 시 전체 화면 상단에 짧게 노출되는 챕터 배너.
 *
 * <p>Umamusume 의 시즌 전환 컷 박자를 참고. 큰 제목 + 부제 + 주차 범위 한 줄.
 * 한 런에서 같은 장은 **한 번만** 노출. 노출 히스토리는 sessionStorage 에 저장해
 * 새로고침·씬 왕복에도 중복 표시 방지.
 *
 * <p>frontier-vanguard 4 장:
 *   W1-20 "원정 준비기", W21-48 "행군기",
 *   W49-72 "거점 확보기", W73-96 "최종 결착기".
 *
 * <p>임의 시나리오 팩 확장을 위해 chapterBoundaries prop 으로 주차 범위를 주입받음.
 */
export type ChapterIntroDef = ScenarioChapterDef;

interface ChapterIntroBannerProps {
    runId: string;
    currentWeek: number;
    chapters: ChapterIntroDef[];
}

export function ChapterIntroBanner({ runId, currentWeek, chapters }: ChapterIntroBannerProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        // 현재 주차가 속한 장을 찾음.
        const hit = chapters.find(c => currentWeek >= c.startWeek && currentWeek <= c.endWeek);
        if (!hit) return;
        // 장 진입 주차(정확히 startWeek)에만 트리거. 장 중간에 씬을 왔다 갔다 할 때마다 뜨면 안 됨.
        if (currentWeek !== hit.startWeek) return;
        // sessionStorage 중복 방지 — 같은 run 의 같은 장은 한 번만.
        const key = `chapter-intro-seen:${runId}:${hit.index}`;
        if (typeof window !== "undefined" && window.sessionStorage?.getItem(key)) return;

        setActiveIndex(hit.index);
        if (typeof window !== "undefined") {
            window.sessionStorage?.setItem(key, "1");
        }

        const timer = window.setTimeout(() => setActiveIndex(null), 3200);
        return () => window.clearTimeout(timer);
    }, [runId, currentWeek, chapters]);

    const active = activeIndex != null ? chapters.find(c => c.index === activeIndex) : null;
    if (!active) return null;

    const accent = toneAccent(active.tone);

    return (
        <AnimatePresence>
            <motion.div
                key={active.index}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.7, ease: tokens.easing.decelerate }}
                className="absolute left-0 right-0 pointer-events-none"
                style={{
                    top: "18%",
                    zIndex: tokens.elevation.modal,
                }}
                role="status"
                aria-live="polite"
            >
                <div
                    className="mx-auto text-center"
                    style={{ width: "min(880px, 92%)" }}
                >
                    {/* 2026-04-23 시네마틱 강화: 상단 chapter 번호 kicker + 이중 ember 라인. */}
                    <motion.div
                        initial={{ opacity: 0, letterSpacing: "0.1em" }}
                        animate={{ opacity: 0.85, letterSpacing: "0.3em" }}
                        transition={{ duration: 0.8, ease: tokens.easing.decelerate }}
                        className="font-body mb-s"
                        style={{
                            color: accent,
                            fontSize: 12,
                            textTransform: "uppercase",
                        }}
                    >
                        제{active.index + 1}장 · {active.startWeek}–{active.endWeek}주
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, letterSpacing: "0.4em", y: 6 }}
                        animate={{ opacity: 1, letterSpacing: "0.08em", y: 0 }}
                        transition={{ duration: 1, ease: tokens.easing.decelerate }}
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: "clamp(40px, 3.6vw, 60px)",
                            lineHeight: 1.08,
                            textShadow: `0 0 36px ${accent}99, 0 0 10px ${accent}66, 0 2px 10px ${tokens.color.ink}`,
                        }}
                    >
                        {active.title}
                    </motion.div>
                    {active.subtitle && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.92 }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                            className="font-body italic mt-s"
                            style={{
                                color: tokens.color.parchmentDim,
                                fontSize: tokens.type.size.body,
                                letterSpacing: "0.04em",
                                lineHeight: tokens.type.lineHeight.normal,
                            }}
                        >
                            {active.subtitle}
                        </motion.div>
                    )}
                    <motion.div
                        initial={{ width: "10%", opacity: 0 }}
                        animate={{ width: "48%", opacity: 0.9 }}
                        transition={{ delay: 0.25, duration: 0.9, ease: tokens.easing.decelerate }}
                        className="mx-auto mt-l"
                        style={{
                            height: 1,
                            background: `linear-gradient(90deg, transparent, ${accent}FF, transparent)`,
                            boxShadow: `0 0 16px ${accent}99`,
                        }}
                    />
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

function toneAccent(tone: ChapterIntroDef["tone"]): string {
    switch (tone) {
        case "steel": return tokens.color.steel;
        case "brass": return tokens.color.brass;
        case "ember": return tokens.color.ember;
        case "burgundy": return tokens.color.burgundy;
        default: return tokens.color.brass;
    }
}

export const FRONTIER_VANGUARD_CHAPTERS: ChapterIntroDef[] = getScenarioChapters("frontier-vanguard");
